import { Button, InputField, Label, RadioGroup } from "@appretio/ui-kit";
import { useState } from "react";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { useTimeTravel } from "../../hooks/manageCubes/useTimeTravel";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import {
  buttonRow,
  errorText,
  fieldGroup,
  flexItem,
  formColumn,
  hintText,
  inlineRow,
  offsetRow,
  simpleFormContainer
} from "./manageCubesOperations.css";

const MAX_SECONDS = 604800; // 7 days

/** Props for the Time Travel operation form, which reverts a cube to a previous point in time. */
interface TimeTravelOperationProps {
  /** The cube ID to time-travel. */
  cubeId: string;
}

export function TimeTravelOperation({ cubeId }: TimeTravelOperationProps) {
  const [mode, setMode] = useState<"offset" | "timestamp">("offset");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [seconds, setSeconds] = useState("");
  const [timestamp, setTimestamp] = useState("");
  const [timestampTime, setTimestampTime] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();

  const timeTravel = useTimeTravel();

  function getTotalSeconds(): number {
    return (
      (parseInt(hours || "0", 10) || 0) * 3600 +
      (parseInt(minutes || "0", 10) || 0) * 60 +
      (parseInt(seconds || "0", 10) || 0)
    );
  }

  function getTimestampISO(): string {
    const time = timestampTime || "00:00:00";
    return `${timestamp}T${time}Z`;
  }

  function validate(): boolean {
    if (mode === "offset") {
      const total = getTotalSeconds();
      if (total <= 0) {
        setError("Offset must be greater than zero.");
        return false;
      }
      if (total > MAX_SECONDS) {
        setError("Offset cannot exceed 7 days (604,800 seconds).");
        return false;
      }
    } else {
      if (!timestamp) {
        setError("Date is required.");
        return false;
      }
      const ts = new Date(getTimestampISO());
      const now = new Date();
      if (ts > now) {
        setError("Cannot time travel to the future.");
        return false;
      }
      const sevenDaysAgo = new Date(now.getTime() - MAX_SECONDS * 1000);
      if (ts < sevenDaysAgo) {
        setError("Cannot time travel more than 7 days back.");
        return false;
      }
    }
    setError(undefined);
    return true;
  }

  function handleSubmit() {
    if (!validate()) return;
    setDialogOpen(true);
  }

  function handleConfirm() {
    if (mode === "offset") {
      timeTravel.mutate(
        {
          mode: "offset",
          cube_id: cubeId,
          offset_seconds: getTotalSeconds()
        },
        {
          onSuccess: (data) => {
            showSuccess(data.message);
            setHours("");
            setMinutes("");
            setSeconds("");
            setDialogOpen(false);
          },
          // The global mutation handler already toasts the failure with the
          // server's reason — a local toast would duplicate it.
          onError: () => {
            setDialogOpen(false);
          }
        }
      );
    } else {
      timeTravel.mutate(
        {
          mode: "timestamp",
          cube_id: cubeId,
          timestamp: getTimestampISO()
        },
        {
          onSuccess: (data) => {
            showSuccess(data.message);
            setTimestamp("");
            setTimestampTime("");
            setDialogOpen(false);
          },
          onError: () => {
            setDialogOpen(false);
          }
        }
      );
    }
  }

  function getConfirmMessage(): string {
    if (mode === "offset") {
      const total = getTotalSeconds();
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const parts = [];
      if (h > 0) parts.push(`${h}h`);
      if (m > 0) parts.push(`${m}m`);
      if (s > 0) parts.push(`${s}s`);
      return `Revert cube to ${parts.join(" ")} ago.`;
    }
    return `Revert cube to ${getTimestampISO()}.`;
  }

  const canSubmit = mode === "offset" ? getTotalSeconds() > 0 : !!timestamp;

  // 7-day window for the Point-in-time picker — min is the UTC date
  // 7 days ago, max is the UTC date today. Picking a date inside the
  // window still lets the user overshoot by time-of-day, but ``validate``
  // catches that at submit with a clearer message than a greyed cell.
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - MAX_SECONDS * 1000);
  const minDate = sevenDaysAgo.toISOString().slice(0, 10);
  const maxDate = now.toISOString().slice(0, 10);

  return (
    <>
      {ToastElement}
      <div className={simpleFormContainer}>
        <div className={formColumn}>
          <p className={hintText}>
            Revert the cube to a previous state — up to 7 days back.
          </p>
          <RadioGroup
            name="time-travel-mode"
            value={mode}
            onChange={(val) => {
              setMode(val as "offset" | "timestamp");
              setError(undefined);
            }}
            items={[
              { label: "Offset", value: "offset" },
              { label: "Point in time", value: "timestamp" }
            ]}
          />

          {mode === "offset" && (
            <div className={offsetRow}>
              <div className={`${fieldGroup} ${flexItem}`}>
                <Label label="Hours" />
                <InputField
                  type="number"
                  placeholder="0"
                  value={hours}
                  onChange={(e) => {
                    setHours(e.target.value);
                    setError(undefined);
                  }}
                />
              </div>
              <div className={`${fieldGroup} ${flexItem}`}>
                <Label label="Minutes" />
                <InputField
                  type="number"
                  placeholder="0"
                  value={minutes}
                  onChange={(e) => {
                    setMinutes(e.target.value);
                    setError(undefined);
                  }}
                />
              </div>
              <div className={`${fieldGroup} ${flexItem}`}>
                <Label label="Seconds" />
                <InputField
                  type="number"
                  placeholder="0"
                  value={seconds}
                  onChange={(e) => {
                    setSeconds(e.target.value);
                    setError(undefined);
                  }}
                />
              </div>
            </div>
          )}

          {mode === "timestamp" && (
            <div className={inlineRow}>
              <div className={`${fieldGroup} ${flexItem}`}>
                <Label label="Date (UTC)" />
                <InputField
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={timestamp}
                  onChange={(e) => {
                    setTimestamp(e.target.value);
                    setError(undefined);
                  }}
                />
              </div>
              <div className={`${fieldGroup} ${flexItem}`}>
                <Label label="Time (UTC)" />
                <InputField
                  type="time"
                  value={timestampTime}
                  onChange={(e) => {
                    setTimestampTime(e.target.value);
                    setError(undefined);
                  }}
                  step="1"
                />
              </div>
            </div>
          )}

          {error && <p className={errorText}>{error}</p>}

          <div className={buttonRow}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              Time Travel
            </Button>
          </div>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Time Travel"
        confirmText="Revert"
        onConfirm={handleConfirm}
        isLoading={timeTravel.isPending}
      >
        <p>{getConfirmMessage()} This operation is irreversible.</p>
      </ConfirmOperationDialog>
    </>
  );
}
